import { FluentProvider, Label, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import React, { useContext } from 'react';
import UserContext from '../../../utils/authorization/UserContext';
import momentUtc from '../../../utils/DateFormatter';
import { IDocument } from '../../../utils/statApi/DocumentApi';
import { StatUser } from '../../../utils/statApi/UsersApi';
import StatDateFormats from '../../../utils/types/DateFormats';
import FileListUpload from '../formFields/fileUpload/fileSelector';
import FileSelectorItem from '../formFields/fileUpload/fileSelectorItem';

interface IDocumentsFormProps {
  documents: IDocument[];
  attachDocument: (files: IDocument[]) => Promise<void>;
  uploadInProgress: (isUploading: boolean) => void;
  deleteDocument: (index: number) => void;
  maxOfOne?: boolean;
  /** When false, hides the upload control so existing documents can only be viewed or removed. */
  allowUpload?: boolean;
  /** Message shown in place of the upload control when uploads are disabled. */
  uploadDisabledMessage?: string;
  documentsLabelText?: string;
  /** Shows the required asterisk on the documents label. */
  documentsRequired?: boolean;
}

const DocumentsForm = ({
  documents,
  attachDocument,
  deleteDocument,
  maxOfOne = false,
  uploadInProgress,
  allowUpload = true,
  uploadDisabledMessage,
  documentsLabelText = 'Documents',
  documentsRequired = false,
}: IDocumentsFormProps): JSX.Element => {
  const user: StatUser = useContext(UserContext);

  const addDocument = async (files: IDocument[]): Promise<void> => {
    attachDocument(files);
  };

  const renderDocumentList = (): JSX.Element => {
    const list = documents || [];
    return list.length ? (
      <Stack tokens={{ childrenGap: 5 }}>
        {list.map((doc: IDocument, index: number) => {
          return (
            <FileSelectorItem
              key={doc.url}
              url={doc.url}
              index={index}
              user={doc.uploader ? doc.uploader : user}
              fileName={doc.title}
              allowDownload
              allowViewInTab
              uiDisabled={false}
              allowDelete
              onRemoveFromSelected={deleteDocument}
              activity={`Uploaded ${momentUtc(doc.uploadDate, StatDateFormats.DayBreakout, true)}`}
            />
          );
        })}
      </Stack>
    ) : (
      <span>A document has not been uploaded yet.</span>
    );
  };

  const headerStyles = { marginTop: 15, marginBottom: 5 };

  return (
    <>
      <Label required={documentsRequired}>{documentsLabelText}</Label>
      {allowUpload ? (
        <FileListUpload
          onFileUploading={(): void => uploadInProgress(true)}
          onFileUploadError={(): void => uploadInProgress(false)}
          onFileUploadComplete={addDocument}
          autoUpload
          allowMultiple={!maxOfOne}
        />
      ) : (
        uploadDisabledMessage && (
          <FluentProvider>
            <MessageBar intent="info" layout="multiline" style={{ marginTop: 10 }}>
              <MessageBarBody>{uploadDisabledMessage}</MessageBarBody>
            </MessageBar>
          </FluentProvider>
        )
      )}
      <h3 style={headerStyles}>Files Uploaded:</h3>
      <Stack tokens={{ childrenGap: 10 }}>{renderDocumentList()}</Stack>
    </>
  );
};

export default DocumentsForm;
